import axiosInstance from './AxiosInstance';

const CompanyUsersService = {
    createUserInCompany: async (companyId, userData) => {
        try {
            const response = await axiosInstance.post(`/company-users/${companyId}/users/create`, userData);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to create user";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    // Assign a role to a user in a company
    assignUserRole: async (companyId, assignmentData) => {
        try {
            const response = await axiosInstance.post(
                `/company-users/${companyId}/users/assign-role`, 
                assignmentData
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to assign role";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    // Get all role assignments for a company
    getCompanyUserAssignments: async (companyId, params = {}) => {
        console.log('getCompanyUserAssignments')
        try {
            const queryParams = new URLSearchParams();
            if (params.userId) queryParams.append('userId', params.userId);
            if (params.status) queryParams.append('status', params.status);
            
            const queryString = queryParams.toString();
            const url = queryString 
                ? `/company-users/${companyId}/user-assignments?${queryString}` 
                : `/company-users/${companyId}/user-assignments`;
            
            console.log('🌐 CompanyUsersService.getCompanyUserAssignments - Calling URL:', url);
            const response = await axiosInstance.get(url);
            console.log('✅ CompanyUsersService.getCompanyUserAssignments - Response:', response.data);
            return response.data;
        } catch (error) {
            console.error('❌ CompanyUsersService.getCompanyUserAssignments - Error:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch user assignments";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // Update an existing role assignment
    updateRoleAssignment: async (companyId, assignmentId, updateData) => {
        try {
            const response = await axiosInstance.put(
                `/company-users/${companyId}/role-assignment/${assignmentId}`,
                updateData
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to update role assignment";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    // Remove a role assignment from a user
    removeRoleAssignment: async (companyId, assignmentId) => {
        try {
            const response = await axiosInstance.delete(
                `/company-users/${companyId}/role-assignment/${assignmentId}`
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to remove role assignment";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    searchCompanyUsers: async (companyId, params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add search parameters
            if (params.search) queryParams.append('search', params.search);
            if (params.page) queryParams.append('page', params.page);
            if (params.limit) queryParams.append('limit', params.limit);
            if (params.status) queryParams.append('status', params.status);
            if (params.roleId) queryParams.append('roleId', params.roleId);
            
            const queryString = queryParams.toString();
            const url = queryString 
                ? `/company-users/${companyId}/users/search?${queryString}` 
                : `/company-users/${companyId}/users/search`;
            
            const response = await axiosInstance.get(url);
            console.log('✅ CompanyUsersService.searchCompanyUsers - Response:', response.data);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to search company users";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // Update company user profile
    updateCompanyUserProfile: async (companyId, assignmentId, userData) => {
        try {
            const response = await axiosInstance.put(
                `/company-users/${companyId}/users/${assignmentId}`,
                userData
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to update user profile";
            const validationErrors = error.response?.data?.errors || [];
            throw {
                message: errorMessage,
                validationErrors: validationErrors,
                status: error.response?.status
            };
        }
    },

    // Get my companies
    getMyCompanies: async () => {
        try {
            const response = await axiosInstance.get('/company-users/my-companies');
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch my companies";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // Get company user by assignment ID (for editing)
    getCompanyUserByAssignmentId: async (companyId, assignmentId) => {
        try {
            const response = await axiosInstance.get(
                `/company-users/${companyId}/users/${assignmentId}/profile`
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch user profile";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // Delete/Remove user from company
    removeUserFromCompany: async (companyId, assignmentId) => {
        try {
            const response = await axiosInstance.delete(
                `/company-users/${companyId}/users/${assignmentId}`
            );
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to remove user from company";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // Get company users statistics (new endpoint: /api/statistics/company-users)
    getCompanyUsersStatistics: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams();
            
            // Add optional query parameters
            if (params.companyId) queryParams.append('companyId', params.companyId);
            if (params.branchId) queryParams.append('branchId', params.branchId);
            if (params.roleId) queryParams.append('roleId', params.roleId);
            
            const queryString = queryParams.toString();
            const url = queryString 
                ? `/statistics/company-users?${queryString}` 
                : `/statistics/company-users`;
            
            const response = await axiosInstance.get(url);
            
            // Return the data directly if success
            if (response.data && response.data.success) {
                return response.data.data;
            }
            
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch SuperAdmin and Admin users";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },

    // Get SuperAdmin and Admin users filtered by company and branch
    getSuperAdminAndAdmins: async (companyId, branchId) => {
        try {
            const queryParams = new URLSearchParams();
            if (companyId) queryParams.append('companyId', companyId);
            if (branchId) queryParams.append('branchId', branchId);
            
            const response = await axiosInstance.get(`/company-users/superadmin-and-admins?${queryParams.toString()}`);
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch statistics";
            throw {
                message: errorMessage,
                status: error.response?.status
            };
        }
    },
};

export default CompanyUsersService;
